import { getTopicPages } from "@/queries/topic";
import { Trans, useLingui } from "@lingui/react/macro";
import { languageKeys, languageMap } from "@op/puck-types";
import {
  AddCard,
  Badge,
  CardGrid,
  IconButton,
  PageCardBasic,
} from "@op/sf-editor";
import { FeatherSettings, FeatherUpload } from "@subframe/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import z from "zod";
import { useDisable } from "@/hooks/useDisable";
import { PageCreateDialog } from "@/components/app/project/PageCreateDialog";
import { useState } from "react";

const paramSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
  languageKey: z.enum(languageKeys),
  topicSlug: z.string(),
});

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/",
)({
  params: paramSchema,
  loader: async ({
    params: { organizationSlug, projectSlug, languageKey, topicSlug },
  }) => {
    const topicPages = await getTopicPages(
      organizationSlug,
      projectSlug,
      languageKey,
      topicSlug,
    );
    return { topicPages };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationSlug, projectSlug, languageKey, topicSlug } =
    Route.useParams();
  const { topicPages } = Route.useLoaderData();

  const [pageCreateDialogOpen, setPageCreateDialogOpen] =
    useState<boolean>(false);

  const queryClient = useQueryClient();

  const { t } = useLingui();

  const { data } = useQuery(
    {
      queryKey: [
        "editor-topic-pages",
        { organizationSlug, projectSlug, languageKey, topicSlug },
      ],
      initialData: topicPages,
      queryFn: () =>
        getTopicPages(organizationSlug, projectSlug, languageKey, topicSlug),
    },
    queryClient,
  );

  const addPageButtonDisabled = useDisable(organizationSlug, projectSlug, [
    "editor",
    "publisher",
    "admin",
  ]);

  return (
    <div className="flex h-full w-full max-w-[1280px] flex-col items-start gap-8 px-4 mx-auto mt-12">
      <div className="flex w-full flex-col items-start gap-6">
        <div className="flex flex-col items-start">
          <span className="text-caption font-caption text-subtext-color">
            {data?.organizationName} - {data?.projectName} -{" "}
            {data?.languageKey ? languageMap[data.languageKey] : ""} -{" "}
            {data?.topicName}
          </span>
          <span className="text-heading-1 font-heading-1 text-default-font">
            <Trans>Pages</Trans>
          </span>
        </div>
        <CardGrid>
          {data?.pages.map((page) => {
            const getStatusBadge = () => {
              if (page.currentVersionId !== null) {
                if (page.changed) {
                  return {
                    variant: "success",
                    children: t`Unpublished updates`,
                  };
                } else {
                  return { variant: "brand", children: t`Published` };
                }
              } else {
                return { variant: "neutral", children: t`Draft` };
              }
            };
            const statusBadge = getStatusBadge();

            return (
              <PageCardBasic
                key={page.id}
                badgeSlot={
                  statusBadge && (
                    <Badge
                      variant={statusBadge.variant as any}
                      icon={<FeatherUpload />}
                      iconRight={null}
                    >
                      {statusBadge.children}
                    </Badge>
                  )
                }
                settingsSlot={
                  <Link
                    to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/settings"
                    params={{
                      organizationSlug,
                      projectSlug,
                      languageKey,
                      topicSlug,
                      pageSlug: page.slug,
                    }}
                    className="contents"
                  >
                    <IconButton
                      variant="brand-tertiary"
                      size="medium"
                      icon={<FeatherSettings />}
                    />
                  </Link>
                }
              >
                <Link
                  to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug"
                  params={{
                    organizationSlug,
                    projectSlug,
                    languageKey,
                    topicSlug,
                    pageSlug: page.slug,
                  }}
                  className="contents"
                >
                  <PageCardBasic.Label>
                    <Trans>Page</Trans>
                  </PageCardBasic.Label>
                  <PageCardBasic.Title>{page.name}</PageCardBasic.Title>
                </Link>
              </PageCardBasic>
            );
          })}
          {!addPageButtonDisabled && (
            <AddCard
              label={t`Add a new page...`}
              onClick={() => setPageCreateDialogOpen(true)}
            />
          )}
        </CardGrid>
      </div>
      <PageCreateDialog
        open={pageCreateDialogOpen}
        onOpenChange={setPageCreateDialogOpen}
        organizationSlug={organizationSlug}
        projectSlug={projectSlug}
        languageKey={languageKey}
        topicSlug={topicSlug}
      />
    </div>
  );
}
